import { inject } from '@angular/core';
import { CanActivateFn, CanMatchFn, Router } from '@angular/router';
import { TenantContextService } from 'shared/core';

/**
 * Frontend half of the vertical gate (tannery ADR-001).
 *
 * Lives in the shared library because every vertical needs the same guard with
 * a different expected value — accessories will call `requireUnitType('accessories')`. The
 * backend middleware remains the authoritative check; this only keeps a user
 * out of a UI their tenant cannot use.
 */
export function requireUnitType(expected: string, fallbackRoute = '/home/denied'): CanActivateFn {
  return () => {
    const tenantContext = inject(TenantContextService);
    const router = inject(Router);

    const actual = tenantContext.getUnitType();
    if (actual?.toLowerCase() === expected.toLowerCase()) {
      return true;
    }

    return router.parseUrl(fallbackRoute);
  };
}

/**
 * Prefer this over `requireUnitType` on any route with `loadChildren`.
 *
 * Measured in the spike: `canActivate` runs *after* the router has already
 * downloaded the lazy chunk, so a rexine tenant fetches the tannery bundle and
 * only then gets bounced. `canMatch` runs before the route is matched, so the
 * chunk is never requested.
 *
 * Because a failed `canMatch` falls through to the next matching route, pair it
 * with a same-path fallback:
 *
 *   { path: 'tannery', canMatch: [matchUnitType('tannery')], loadChildren: ... }
 *   { path: 'tannery', redirectTo: '/home/denied/tannery' }
 *
 * The fallback must be an absolute redirect: a relative `redirectTo` keeps a
 * deep link's leftover segments, and `denied/tannery/masters` matches nothing.
 */
export function matchUnitType(expected: string): CanMatchFn {
  return () => {
    const actual = inject(TenantContextService).getUnitType();
    return actual?.toLowerCase() === expected.toLowerCase();
  };
}
